import {
  Component,
  ChangeDetectionStrategy,
  inject,
  input,
  signal,
  computed,
  effect,
} from '@angular/core';
import { TranslocoDirective, TranslocoService } from '@ngneat/transloco';
import {
  currencyFormat,
  getCurrencyLocaleDataByCountryCode,
  EndOrderDetails,
} from '@pedix-workspace/utils';
import { BillingMode } from '../create-voucher-modal.component';
import { generateBrowserPdf } from '@pedix-workspace/shared-pdf';
import {
  ARCA_CURRENCY_FORMAT_OPTIONS,
  ArcaIntegrationDetails,
  ArcaDocumentType,
  ArcaVatCondition,
  getArcaDocumentTypeName,
  generateArcaVoucherPdf,
  generateTaxVoucherArca,
  getArcaVatConditionName,
  TaxVoucherArca,
} from '@pedix-workspace/integrations-arca';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Integration } from '@pedix-workspace/integrations-shared';
import { PdfModalViewerComponent } from '@pedix-workspace/angular-ui-pdf';
import { IconEyeComponent } from '@pedix-workspace/angular-ui-icons';
import { ButtonComponent } from '@pedix-workspace/angular-ui-button';
import { SelectBoxComponent } from '@pedix-workspace/angular-ui-form-reactive';
import { FormsModule } from '@angular/forms';
import { NgTemplateOutlet } from '@angular/common';
import { endOrderLineItems } from '@pedix-workspace/orders';
import { ApiV1_Establishment } from '@pedix-workspace/api-v1';

const currencyFormatFn = (amount: number) => {
  return currencyFormat(
    amount,
    getCurrencyLocaleDataByCountryCode('AR'),
    ARCA_CURRENCY_FORMAT_OPTIONS,
  );
};

@UntilDestroy()
@Component({
  selector: 'app-create-voucher-modal-preview',
  templateUrl: './create-voucher-modal-preview.component.html',
  styleUrls: ['./create-voucher-modal-preview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    TranslocoDirective,
    PdfModalViewerComponent,
    IconEyeComponent,
    ButtonComponent,
    SelectBoxComponent,
    FormsModule,
    NgTemplateOutlet,
  ],
})
export class CreateVoucherModalPreviewComponent {
  private t = inject(TranslocoService);

  readonly establishment = input.required<ApiV1_Establishment>();
  readonly electronicBillingIntegration = input.required<Integration>();
  readonly selectedOrders = input.required<EndOrderDetails[]>();
  readonly billingMode = input.required<BillingMode>();
  readonly groupingConceptName = input.required<string>();
  readonly groupByOrder = input.required<boolean>();
  readonly numberToVouchersToGenerate = input.required<number>();

  readonly pdfPreviewSrc = signal<string | null>(null);
  readonly generatingPreview = signal(false);
  readonly loadingPreview = signal(false);
  readonly selectedOrderId = signal<string | null>(null);

  readonly accountDetails = computed(
    () => <ArcaIntegrationDetails>this.electronicBillingIntegration().details.accountDetails,
  );
  readonly taxVoucher = computed(() => {
    const orderDetails =
      this.billingMode() === 'combined' ? this.selectedOrders() : [this.selectedOrder()!];
    return generateTaxVoucherArca({
      id: '',
      operation: 'bill',
      establishment: this.establishment(),
      accountDetails: this.accountDetails(),
      orderDetails,
      taxVoucherOptions: {
        groupingConceptName: this.groupingConceptName(),
        groupByOrder: this.groupByOrder(),
        printCopies: this.accountDetails().printCopies,
      },
      voucherNumber: 'XXXXXXXX' as any,
      cae: '',
      caeFchVto: '',
      lineItemsGenerator: endOrderLineItems,
    });
  });
  readonly orderDetailsSelectorOptions = computed(() => {
    return this.selectedOrders().map(order => {
      const orderKeyValue = [];

      if (order.billingInformation?.vatCondition) {
        orderKeyValue.push(
          getArcaVatConditionName(order.billingInformation?.vatCondition as ArcaVatCondition),
        );
      }
      if (order.billingInformation?.name) {
        orderKeyValue.push(order.billingInformation?.name);
      } else {
        orderKeyValue.push(order.personalInformation?.name);
      }
      if (order.billingInformation?.documentNumber) {
        orderKeyValue.push(
          getArcaDocumentTypeName(order.billingInformation?.documentType as ArcaDocumentType) +
            ' ' +
            order.billingInformation?.documentNumber,
        );
      }

      return {
        orderKey: `#${order.sequenceId.toString().padStart(7, '0')}`,
        orderKeyValue: orderKeyValue.join(' - '),
        id: order.id,
      };
    });
  });
  readonly selectedOrder = computed(() => {
    return this.selectedOrders().find(orderDetails => orderDetails.id === this.selectedOrderId());
  });

  constructor() {
    effect(
      () => {
        this.selectedOrderId.set(this.selectedOrders()[0].id!);
      },
      { allowSignalWrites: true },
    );
  }

  async createPdfPreview(taxVoucher: TaxVoucherArca) {
    const pdfDefinition = await generateArcaVoucherPdf({
      taxVoucher,
      options: {
        watermarkText: 'BORRADOR NO FISCAL',
        currencyFormatFn,
      },
    });

    const result = await generateBrowserPdf(pdfDefinition);

    return new Promise<string>(resolve => {
      result.getDataUrl(url => {
        resolve(url);
      });
    });
  }

  async onPreviewVoucher() {
    this.generatingPreview.set(true);

    const pdfDataUrl = await this.createPdfPreview(this.taxVoucher());

    this.pdfPreviewSrc.set(pdfDataUrl);

    this.generatingPreview.set(false);
  }

  onError(error: any) {
    this.pdfPreviewSrc.set(null);
  }
}
